import {toast} from '@wandb/components';
import {useCallback} from 'react';
import {useParams} from 'react-router';

import {urlPrefixed} from '../../config';
import {recordRumError} from '../../util/datadog/datadogRum';
import {useNavigate} from '../../util/react-router-v6-shim';
import {
  marimoNotebookRuntimePath,
  marimoNotebookViewPath,
  type NotebookRuntimeTarget,
} from './notebookPaths';

type NotebookProject = {
  entityName: string;
  projectName: string;
};

type OpenMarimoNotebookRuntimeOptions = {
  target?: NotebookRuntimeTarget;
  /** Open in a new browser tab, leaving the current page mounted. */
  newTab?: boolean;
  readOnly?: boolean;
};

type OpenMarimoNotebookRuntime = (
  notebookId: string,
  // optional project to prefer over url params
  project?: NotebookProject,
  options?: OpenMarimoNotebookRuntimeOptions
) => void;

export function useOpenMarimoNotebookRuntime(): OpenMarimoNotebookRuntime {
  // TODO: this may be called outside of available url params, refactor usage
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  const navigate = useNavigate();

  return useCallback(
    (
      notebookId: string,
      project?: NotebookProject,
      {
        target = 'project',
        newTab = false,
        readOnly = false,
      }: OpenMarimoNotebookRuntimeOptions = {}
    ) => {
      const notebookPath = readOnly
        ? marimoNotebookViewPath
        : marimoNotebookRuntimePath;
      const path = notebookPath({
        entityName: project?.entityName ?? entityName,
        notebookId,
        projectName: project?.projectName ?? projectName,
        target,
      });
      if (!newTab) {
        navigate(path);
        return;
      }
      const runtimeWindow = window.open(urlPrefixed(path), '_blank');
      if (runtimeWindow == null) {
        const message = 'Unable to open notebook window';
        toast.error(message);
        recordRumError(new Error(message), {
          context: {message: 'The browser blocked the notebook window'},
        });
        return;
      }
      runtimeWindow.opener = null;
    },
    [entityName, navigate, projectName]
  );
}
